import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injectable,
  Input,
  NgModule,
  Optional,
  Output,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPaginatorModule, PageEvent, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

export interface PageChangeEvent<T> extends PageEvent {
  nextToken: T;
}

@Injectable()
export class CustomPaginatorIntl extends MatPaginatorIntl {
  override firstPageLabel = '';
  override previousPageLabel = '';
  override nextPageLabel = '';
  override lastPageLabel = '';
}

@Component({
    selector: 'ts-token-paginator',
    templateUrl: './token-paginator.component.html',
    styleUrls: ['./token-paginator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: MatPaginatorIntl, useClass: CustomPaginatorIntl }],
    standalone: false
})
export class TokenPaginatorComponent<T> {
  @Input() previousTokens: T[];
  @Input() lastItem: T;
  @Input() length: number;
  @Input() pageSize: number;
  @Input() hidePageSize: boolean;
  @Input() disabled: boolean;
  @Input() hideRangeLabel = false;

  @Optional() @Input() pageSizeOptions: number[] = [];
  @Optional() @Input() counting = false;

  @Output() pageChange = new EventEmitter();
  @Output() pageSizeChange = new EventEmitter();

  public get currentPage(): number {
    return this.previousTokens.length - 1;
  }

  public get showPageSizeSelector(): boolean {
    return this.pageSizeOptions && this.pageSizeOptions.length > 1;
  }

  public get rangeLabel(): string {
    if (this.length === 0 || this.pageSize === 0) {
      return `0 of ${this.length}`;
    }
    const startIndex = Math.max(this.currentPage * this.pageSize + 1, 1);
    const endIndex = Math.min(startIndex + this.pageSize - 1, this.length);
    return `${startIndex} - ${endIndex} of ${this.length}`;
  }

  public emitPageChangeEvent(event: PageEvent) {
    if (event.pageSize !== this.pageSize) return this.pageSizeChange.emit(event.pageSize);

    const isPreviousPage = event.previousPageIndex > event.pageIndex;
    const isNextPage = event.previousPageIndex < event.pageIndex;

    const nextToken = isPreviousPage
      ? this.previousTokens.at(event.pageIndex)
      : isNextPage
      ? this.lastItem
      : this.previousTokens.pop();

    this.pageChange.emit({ ...event, nextToken });
  }
}

@NgModule({
  imports: [CommonModule, MatFormFieldModule, MatPaginatorModule, MatSelectModule, MatTooltipModule, TranslateModule],
  declarations: [TokenPaginatorComponent],
  exports: [TokenPaginatorComponent],
})
export class TokenPaginatorComponentModule {}
